import {
    forwardRef,
    useImperativeHandle,
    useState,
    useContext,
    useRef,
} from 'react'

import TextInput from '../../../../Components/TextInput'
import ApplicantDataFormContext from '../../../../Contexts/ApplicantDataFormContext'
import formatDateDynamic from '../../../../helpers/format-date-dynamic'

// Name validator
const nameValidator = (val) => /^[\p{L} '-]{2,50}$/u.test(val.trim())

const IndiaConsentForm = forwardRef(
    ({ data, company, refToPrint }, ref) => {

        const applicantDataForm = useContext(ApplicantDataFormContext)

        const [agreeChecked, setAgreeChecked] = useState(data?.agreeChecked ?? false)
        const [lastFourGovId, setLastFourGovId] = useState(data?.lastFourGovId ?? '')
        const [place, setPlace] = useState(data?.place ?? '')
        const [error, setError] = useState("")

        const todayDate = formatDateDynamic()

        const fullName = applicantDataForm.current["fullName"]
        const fullNameRef = useRef()

        useImperativeHandle(ref, () => ({
            validateAndGetData: () => {
                let isValid = true

                const fullNameData = fullNameRef.current.validateAndGetData()

                if (!agreeChecked) {
                    isValid = false
                    setError("You must agree to proceed with electronic background verification.")
                }

                if (isValid && lastFourGovId.length !== 4) {
                    isValid = false
                    setError("Please enter the last 4 digits of your Aadhaar / Government ID.")
                }

                if (isValid && !place.trim()) {
                    isValid = false
                    setError("Please enter the Location.")
                }

                if (isValid && !fullNameData.isValid) {
                    isValid = false
                }

                if (isValid && fullName !== fullNameData.value) {
                    isValid = false
                    setError("Candidate name does not match the name provided earlier.")
                }

                if (isValid) {
                    setError("")
                }

                applicantDataForm.current["govIdLastFourDigit"] = lastFourGovId

                return {
                    isValid,
                    value: {
                        agreeChecked,
                        lastFourGovId,
                        fullName,
                        place,
                        date: todayDate,
                    },
                }
            },
        }))

        return (
            <div className="max-w-3xl mx-auto px-4 py-10">
                <div ref={refToPrint} className="flex flex-col space-y-6">

                    <h1 className="text-2xl font-bold text-gray-800 text-center">
                        Authorization and Consent for Background Verification
                    </h1>

                    <div className="space-y-4 text-gray-700 text-sm sm:text-base">

                        <p>
                            <strong>To:</strong> <strong>VerifiedID</strong> (Platform Provider) and{" "}
                            <strong>{company?.name}</strong> (Requesting Entity)
                        </p>

                        <h2 className="font-semibold text-gray-800">1. Notice of Collection</h2>
                        <p>
                            I, <strong>{fullName}</strong>, hereby acknowledge that for the purpose
                            of my application for employment or engagement, <strong>VerifiedID</strong> and its
                            designated partners
                            will conduct a background verification.
                        </p>

                        <h2 className="font-semibold text-gray-800">2. Scope of Verification</h2>
                        <p>I provide my express consent to verify the following information as required by the Requesting Entity,
                            including but not limited to:</p>
                        <ul className="list-disc list-inside space-y-1 pl-5">
                            <li>
                                <strong>Identity Verification:</strong> Authentication of government-issued IDs (Aadhaar, PAN, Voter ID,
                                Passport, or DL).
                            </li>
                            <li>
                                <strong>Address Verification:</strong> Verification of current and/or permanent address through physical site
                                visits, digital geolocation, or utility bill/official document authentication.
                            </li>
                            <li><strong>Education:</strong> Verification of degrees/diplomas from schools, colleges, and universities.</li>
                            <li><strong>Employment:</strong> Verification of previous tenure, designation, and reason for leaving.</li>
                            <li>
                                <strong>Criminal Records:</strong> Search of National Judicial Data Grid (NJDG), police records, and
                                court filings across India.
                            </li>
                            <li>
                                <strong>Global Database:</strong> Searches against global sanctions, AML (Anti-Money Laundering), and
                                OFAC lists.
                            </li>
                            <li>
                                <strong>Other Checks:</strong> Drug Test, Credit Checks, etc. if applicable.
                            </li>
                        </ul>

                        <h2 className="font-semibold text-gray-800">
                            3. Data Privacy & DPDPA (2023) Compliance
                        </h2>
                        <p>
                            In accordance with the <strong>Digital Personal Data Protection Act, 2023:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-1 pl-5">
                            <li><strong>Purpose:</strong> My data is collected solely for background screening and identity assurance.</li>
                            <li>
                                <strong>Storage:</strong> I understand my data may be stored on secure servers (including those located
                                outside India, subject to Central Government cross-border transfer rules).
                            </li>
                            <li>
                                <strong>Retention:</strong> VerifiedID will retain my information as long as necessary to provide the
                                &quot;Shareable Report&quot; service or as required by law.
                            </li>
                            <li>
                                <strong>Right to Withdraw: </strong>
                                I understand I may withdraw this consent at any time by contacting{" "}
                                <a
                                    href="mailto:screening@verifiedid.co"
                                    className="text-blue-600 underline"
                                >
                                    screening@verifiedid.co
                                </a>
                                , though doing so may result in the termination of my
                                employment application.
                            </li>
                            <li><strong>Accuracy:</strong> I represent that all information provided by me is true and accurate.</li>
                        </ul>

                        <h2 className="font-semibold text-gray-800">
                            4. Authorization for Third Parties
                        </h2>
                        <p>
                            I hereby authorize all individuals, companies, educational institutions, law enforcement
                            agencies, and government authorities to release information requested by VerifiedID for the
                            purposes of this BGV. A digital or scanned copy of this authorization shall be as valid as the
                            original.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                            <TextInput
                                ref={fullNameRef}
                                title="Candidate Name"
                                isMandatory={true}
                                data={fullName}
                                validator={nameValidator}
                            />

                            <div>
                                <label className="text-sm text-gray-600 font-medium">
                                    Aadhaar / Gov ID No (Last 4 digits)*
                                </label>
                                <input
                                    value={lastFourGovId}
                                    onChange={(e) => setLastFourGovId(e.target.value)}
                                    maxLength={4}
                                    className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2"
                                    placeholder="1234"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600 font-medium">Date</label>
                                <input
                                    value={todayDate}
                                    readOnly
                                    className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600 font-medium">Location*</label>
                                <input
                                    value={place}
                                    onChange={(e) => setPlace(e.target.value)}
                                    className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2"
                                    placeholder="City, State"
                                />
                            </div>
                        </div>

                        <label className="flex items-start space-x-2 pt-4">
                            <input
                                type="checkbox"
                                checked={agreeChecked}
                                onChange={() => setAgreeChecked(!agreeChecked)}
                                className="mt-1"
                            />
                            <span>
                                I AGREE to proceed with electronic background verification.
                            </span>
                        </label>

                    </div>
                </div>

                {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                )}
            </div>
        )
    }
)

export default IndiaConsentForm
